import { ComponentProps, FC, ReactNode, Ref, useMemo } from 'react';

import classNames from 'classnames';

import Loader, { LoaderProps } from '../Loader';

import './Container.scss';

import {
  ContainerStateType,
  ElevationHeight,
  MarginOptions,
  PrismFill,
  SizeOptions,
  SpacingOptions,
  TestMetadata,
  WidthOptions,
} from '../types';

export interface ContainerProps extends ComponentProps<'div'> {
  /** Elements to Wrap */
  children: ReactNode;
  /** Class name */
  className?: string;
  /** Controls the fill of the container: transparent, white (default), gray, theme */
  fill?: 'transparent' | 'error-light' | PrismFill;
  /** Controls the padding: none, normal (default), tight, loose, full, page. Either set one value to set all positions or four (T-R-B-L) */
  padding?: SpacingOptions | SpacingOptions[];
  /** Controls the margin: none, normal (default), tight, loose, full, page, centered. Either set one value to set all positions or four (T-R-B-L) */
  margin?: MarginOptions | SpacingOptions[];
  /** Controls the width of the stroke on the container: none (default), standard, thin, thick */
  strokeWidth?: WidthOptions;
  /** Controls the color of the stroke, prism system colors with black by default */
  strokeColor?: PrismFill;
  /** Drives corner radius and geometric shape: none, small (default), medium, large  */
  cornerRadius?: 'none' | SizeOptions;
  /** Creates visual hierarchy by using shadow and Z-depth: none, low, mid (default), high  */
  elevation?: ElevationHeight;
  /** Event to fire when the card is clicked */
  onClick?: () => void;
  /** Ref for container div */
  containerRef?: Ref<HTMLDivElement>;
  /** Height of the container */
  height?: 'auto' | 'full';
  /** Test IDs for sections of Container */
  testMetadata?: TestMetadata;
  /** Container state props */
  containerStateProps?: ContainerStateProps;
}

export interface ContainerStateProps {
  /** The current state of the container */
  containerState?: ContainerStateType;
  /** An optional override message to display */
  message?: string;
  /** Loader size to be used for loading state */
  loaderSize?: LoaderProps['size'];
}

const defaultMessage: {
  [key in ContainerStateType]: string;
} = {
  loading: 'Loading...',
  error: 'Something went wrong when fetching data',
  empty: 'No results',
};

const Container: FC<ContainerProps> = ({
  children,
  className,
  padding = 'none',
  margin = 'none',
  strokeWidth = 'none',
  strokeColor = 'black',
  cornerRadius = 'none',
  elevation = 'none',
  fill = 'transparent',
  height = 'auto',
  containerRef,
  testMetadata,
  containerStateProps,
  ...otherProps
}) => {
  const containerClassNames = useMemo(
    () =>
      classNames(
        'sb-container',
        ['sb-container_size-' + height],
        ['sb-container_corner-radius-' + cornerRadius],
        ['sb-container_elevation-' + elevation],
        ['sb-container_fill-' + fill],
        ['sb-container_stroke-width-' + strokeWidth],
        ['sb-container_stroke-color-' + strokeColor],
        {
          /** Only add state class when its set */
          'sb-container_state':
            containerStateProps &&
            containerStateProps?.containerState !== undefined,
          ['sb-container_state-' + containerStateProps?.containerState]:
            containerStateProps &&
            containerStateProps?.containerState !== undefined,

          /** If padding is not array - just set the class, ie sb-container_padding-loose */
          ['sb-container_padding-' + padding]: !Array.isArray(padding),

          /** Else go through top-right-bottom-left and set, ie sb-container_padding-top-loose */
          ['sb-container_padding-top-' + padding[0]]:
            Array.isArray(padding) && padding.length > 0,
          ['sb-container_padding-right-' + padding[1]]:
            Array.isArray(padding) && padding.length > 1,
          ['sb-container_padding-bottom-' + padding[2]]:
            Array.isArray(padding) && padding.length > 2,
          ['sb-container_padding-left-' + padding[3]]:
            Array.isArray(padding) && padding.length > 3,

          /** If margin is not array - just set the class, ie sb-container_margin-loose */
          ['sb-container_margin-' + margin]: !Array.isArray(margin),

          /** Else go through top-right-bottom-left and set, ie sb-container_margin-top-loose */
          ['sb-container_margin-top-' + margin[0]]:
            Array.isArray(margin) && margin.length > 0,
          ['sb-container_margin-right-' + margin[1]]:
            Array.isArray(margin) && margin.length > 1,
          ['sb-container_margin-bottom-' + margin[2]]:
            Array.isArray(margin) && margin.length > 2,
          ['sb-container_margin-left-' + margin[3]]:
            Array.isArray(margin) && margin.length > 3,
        },
        className,
      ),
    [
      strokeWidth,
      strokeColor,
      cornerRadius,
      elevation,
      fill,
      className,
      padding,
      margin,
      height,
      containerStateProps,
    ],
  );

  return (
    <div
      {...otherProps}
      className={containerClassNames}
      ref={containerRef}
      {...testMetadata}
    >
      {containerStateProps?.containerState && (
        <div>
          {containerStateProps.message ??
            defaultMessage[containerStateProps.containerState]}
          {containerStateProps.containerState === 'loading' && (
            <Loader size={containerStateProps?.loaderSize || 'large'} />
          )}
        </div>
      )}

      {!containerStateProps?.containerState && children}
    </div>
  );
};

export default Container;
